import * as React from "react"
import { Slot } from "@radix-ui/react-slot"
import { cva, type VariantProps } from "class-variance-authority"

import { cn } from "@/lib/utils"

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--azure-focus-ring)] focus-visible:ring-offset-2 focus-visible:ring-offset-white disabled:pointer-events-none disabled:opacity-50",
  {
    variants: {
      variant: {
        default: "rounded-[var(--border-radius-medium)] border border-[var(--azure-primary)] bg-[var(--azure-primary)] text-white shadow-[var(--shadow-light)] hover:bg-[var(--azure-primary-hover)]",
        destructive:
          "rounded-[var(--border-radius-medium)] border border-[var(--semantic-error)] bg-[var(--semantic-error)] text-white shadow-[var(--shadow-light)] hover:bg-[var(--semantic-error)]/90",
        outline:
          "rounded-[var(--border-radius-medium)] border border-[var(--neutral-gray-50)] bg-white text-[var(--neutral-gray-120)] hover:bg-[var(--surface-muted)]",
        secondary:
          "rounded-[var(--border-radius-medium)] border border-[var(--neutral-gray-40)] bg-[var(--surface-muted)] text-[var(--neutral-gray-120)] hover:bg-[var(--neutral-gray-20)]",
        ghost: "rounded-[var(--border-radius-medium)] text-[var(--neutral-gray-120)] hover:bg-[var(--surface-muted)]",
        link: "text-[var(--azure-primary)] hover:underline underline-offset-4",
      },
      size: {
        default: "h-[var(--control-height-standard)] px-[var(--control-padding-x)] text-sm",
        sm: "h-[var(--control-height-standard)] px-[var(--control-padding-x-tight)] text-sm",
        lg: "h-[var(--control-height-large)] px-4 text-sm",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
)

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : "button"
    return (
      <Comp
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref}
        {...props}
      />
    )
  }
)
Button.displayName = "Button"

export { Button, buttonVariants }
